import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getMonthlyRevenue } from '../api/monthlyRevenue';
import type { MonthlyRevenue } from '../types/monthlyRevenue';
import styles from './MonthlyRevenue.module.css';

/**
 * Monthly Revenue page.
 *
 * Three bar charts that answer "how much did we make each month?":
 *  1. Shopify (hardware) revenue per month.
 *  2. App (Stripe) revenue per month, stacked lifetime + subscription.
 *  3. Combined total per month, stacked Shopify + App.
 *
 * App revenue is ACTUAL Stripe cash (not the projection on Accounts
 * Overview). Mobile IAP revenue is not included.
 */

const WINDOWS: ReadonlyArray<{ label: string; months: number }> = [
  { label: '6m', months: 6 },
  { label: '12m', months: 12 },
  { label: '24m', months: 24 },
];

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

const COLORS = {
  shopify: '#22c55e',
  lifetime: '#a855f7',
  subscription: '#3b82f6',
  app: '#f59e0b',
} as const;

function fmtMoney(n: number | null | undefined, currency: string): string {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'never';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

/** Compact currency for on-bar total labels (e.g. "$1.2K"). Empty for zero. */
function fmtCompact(n: number | null | undefined, currency: string): string {
  if (n == null || Number.isNaN(n) || n === 0) return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return `$${Math.round(n)}`;
  }
}

interface TooltipItem {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

/**
 * Custom tooltip for the stacked charts: lists each segment and a bold
 * Total row, so the combined figure is visible on hover.
 */
function StackTooltip(props: {
  active?: boolean;
  payload?: TooltipItem[];
  label?: string | number;
  currency: string;
}) {
  const { active, payload, label, currency } = props;
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce(
    (sum, item) => sum + (Number(item.value) || 0),
    0
  );
  return (
    <div style={{ ...TOOLTIP_STYLE, padding: '8px 10px', color: '#e5e7eb' }}>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>{label}</div>
      {payload.map((item) => (
        <div key={String(item.dataKey ?? item.name)} style={{ color: item.color }}>
          {item.name}: {fmtMoney(Number(item.value), currency)}
        </div>
      ))}
      <div
        style={{
          marginTop: 4,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          paddingTop: 4,
          fontWeight: 600,
        }}
      >
        Total: {fmtMoney(total, currency)}
      </div>
    </div>
  );
}

interface ChartRow {
  label: string;
  shopify: number;
  lifetime: number;
  subscription: number;
  app: number;
  combined: number;
}

export default function MonthlyRevenue() {
  const [months, setMonths] = useState<number>(12);
  const [data, setData] = useState<MonthlyRevenue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowMonths: number, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMonthlyRevenue(windowMonths, refresh);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load monthly revenue';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(months);
  }, [months, load]);

  const currency = data?.summary.currency ?? 'USD';

  const rows = useMemo<ChartRow[]>(() => {
    if (!data) return [];
    return data.labels.map((label, i) => ({
      label,
      shopify: data.shopify.data[i] ?? 0,
      lifetime: data.app.lifetime[i] ?? 0,
      subscription: data.app.subscription[i] ?? 0,
      app: data.app.total[i] ?? 0,
      combined: data.combined[i] ?? 0,
    }));
  }, [data]);

  const moneyAxis = useCallback(
    (n: number) => {
      if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
      return `${n}`;
    },
    []
  );

  const moneyTip = useCallback(
    (value: number) => fmtMoney(value, currency),
    [currency]
  );

  const labelFmt = useCallback(
    (value: unknown) => fmtCompact(Number(value), currency),
    [currency]
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Monthly Revenue</h1>
          <p className={styles.subtitle}>
            Actual revenue per month across both streams — Shopify (hardware)
            and the app (Stripe: lifetime + subscription). Mobile in-app
            purchases aren't included. This is real collected cash, not the
            projection on Accounts Overview.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatRelativeTime(lastUpdated)}
          </span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(months, true)}
            disabled={loading}
          >
            <span
              className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
            >
              ↻
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.months}
              type="button"
              onClick={() => setMonths(w.months)}
              className={`${styles.pill} ${months === w.months ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {data?.shopify.note && (
        <div className={styles.warning}>{data.shopify.note}</div>
      )}
      {data && !data.app.available && (
        <div className={styles.warning}>
          App (Stripe) revenue unavailable: {data.app.reason ?? 'unknown error'}
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.combined_total, currency)}
          </div>
          <div className={styles.statLabel}>Combined total ({months}m)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.shopify_total, currency)}
          </div>
          <div className={styles.statLabel}>Shopify total</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.app_total, currency)}
          </div>
          <div className={styles.statLabel}>App total</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.app_lifetime_total, currency)}
          </div>
          <div className={styles.statLabel}>App · lifetime</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.app_subscription_total, currency)}
          </div>
          <div className={styles.statLabel}>App · subscription</div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading monthly revenue…</div>
      ) : data ? (
        <>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Combined revenue / month</h3>
              <span className={styles.sectionTotal}>
                {fmtMoney(data.summary.combined_total, currency)}
              </span>
            </div>
            <div className={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                  <YAxis tickFormatter={moneyAxis} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                  <Tooltip
                    content={(p) => (
                      <StackTooltip
                        active={p.active}
                        payload={p.payload as unknown as TooltipItem[]}
                        label={p.label}
                        currency={currency}
                      />
                    )}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.8125rem' }} />
                  <Bar dataKey="shopify" name="Shopify" stackId="combined" fill={COLORS.shopify} />
                  <Bar dataKey="app" name="App" stackId="combined" fill={COLORS.app} radius={[3, 3, 0, 0]}>
                    <LabelList
                      dataKey="combined"
                      position="top"
                      formatter={labelFmt}
                      fill="#e5e7eb"
                      fontSize={11}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.chartsGrid}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Shopify / month</h3>
                <span className={styles.sectionTotal}>
                  {fmtMoney(data.summary.shopify_total, currency)}
                </span>
              </div>
              <div className={styles.chartWrap}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tickFormatter={moneyAxis} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value) => [moneyTip(Number(value)), 'Shopify']}
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    />
                    <Bar dataKey="shopify" name="Shopify" fill={COLORS.shopify} radius={[3, 3, 0, 0]}>
                      <LabelList
                        dataKey="shopify"
                        position="top"
                        formatter={labelFmt}
                        fill="#e5e7eb"
                        fontSize={10}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>App / month</h3>
                <span className={styles.sectionTotal}>
                  {fmtMoney(data.summary.app_total, currency)}
                </span>
              </div>
              <div className={styles.chartWrap}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tickFormatter={moneyAxis} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip
                      content={(p) => (
                        <StackTooltip
                          active={p.active}
                          payload={p.payload as unknown as TooltipItem[]}
                          label={p.label}
                          currency={currency}
                        />
                      )}
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '0.8125rem' }} />
                    <Bar dataKey="subscription" name="Subscription" stackId="app" fill={COLORS.subscription} />
                    <Bar dataKey="lifetime" name="Lifetime" stackId="app" fill={COLORS.lifetime} radius={[3, 3, 0, 0]}>
                      <LabelList
                        dataKey="app"
                        position="top"
                        formatter={labelFmt}
                        fill="#e5e7eb"
                        fontSize={10}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className={styles.notes}>
        <h4 className={styles.notesTitle}>Notes</h4>
        <ul>
          <li>
            <strong>App revenue is actual Stripe cash</strong> (charges net of
            refunds), classified as <strong>subscription</strong> when tied to
            an invoice and <strong>lifetime</strong> otherwise.
          </li>
          <li>
            <strong>Mobile</strong> (Apple/Google) in-app purchases are not
            included — no dollar amounts are stored for them.
          </li>
          <li>
            Shopify months may be capped to ~60 days until the{' '}
            <code>read_all_orders</code> scope is approved.
          </li>
        </ul>
      </div>
    </div>
  );
}
